import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  EquipmentCalibration,
  EquipmentCalibrationAttachment,
  EquipmentCalibrationPayload,
  EquipmentDocument,
  EquipmentMainPart,
  EquipmentMainPartPayload,
  EquipmentRepair,
  EquipmentRepairPayload,
  ManufacturingEquipment,
  ManufacturingEquipmentPayload,
  ManufacturingStockPart,
  ManufacturingStockPartPayload,
  EquipmentManagementStandard,
  EquipmentManagementStandardPayload,
  EquipmentInspectionSheet,
  EquipmentInspectionSheetPayload,
  EquipmentType,
} from '../types/manufacturingEquipment';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export function equipmentApiBase(type: EquipmentType = 'MANUFACTURING') {
  return type === 'INSPECTION' ? '/inspection-equipments' : '/manufacturing-equipments';
}

export interface EquipmentCalendarItem {
  kind: string;
  date: string;
  equipmentId: number;
  equipmentName?: string | null;
  equipmentType?: string | null;
  calibrationId?: number | null;
}

export async function fetchEquipmentCalendar(
  from: string,
  to: string
): Promise<EquipmentCalendarItem[]> {
  const { data } = await apiClient.get<EquipmentCalendarItem[]>('/equipment/calendar', {
    params: { from, to },
  });
  return data;
}

export async function fetchNextEquipmentCode(
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    `${equipmentApiBase(equipmentType)}/suggestions/equipment-code`
  );
  return data;
}

export async function fetchManufacturingEquipments(
  keyword?: string,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<ManufacturingEquipment[]> {
  const { data } = await apiClient.get<ManufacturingEquipment[]>(equipmentApiBase(equipmentType), {
    params: keyword?.trim() ? { q: keyword.trim() } : {},
  });
  return data;
}

export async function fetchManufacturingEquipment(
  equipmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<ManufacturingEquipment> {
  const { data } = await apiClient.get<ManufacturingEquipment>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}`
  );
  return data;
}

export async function createManufacturingEquipment(
  payload: ManufacturingEquipmentPayload,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<ManufacturingEquipment> {
  const { data } = await apiClient.post<ManufacturingEquipment>(equipmentApiBase(equipmentType), {
    ...payload,
    equipmentType,
  });
  return data;
}

export async function updateManufacturingEquipment(
  equipmentId: number,
  payload: ManufacturingEquipmentPayload,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<ManufacturingEquipment> {
  const { data } = await apiClient.patch<ManufacturingEquipment>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}`,
    payload
  );
  return data;
}

export async function deactivateManufacturingEquipment(
  equipmentId: number,
  reason: string,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<ManufacturingEquipment> {
  const { data } = await apiClient.post<ManufacturingEquipment>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/deactivate`,
    { reason }
  );
  return data;
}

export async function activateManufacturingEquipment(
  equipmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<ManufacturingEquipment> {
  const { data } = await apiClient.post<ManufacturingEquipment>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/activate`
  );
  return data;
}

export async function deleteManufacturingEquipment(
  equipmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<void> {
  await apiClient.delete(`${equipmentApiBase(equipmentType)}/${equipmentId}`);
}

export async function uploadManufacturingEquipmentImage(
  equipmentId: number,
  image: File,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<ManufacturingEquipment> {
  const formData = new FormData();
  formData.append('image', image);
  const { data } = await apiClient.post<ManufacturingEquipment>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/image`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteManufacturingEquipmentImage(
  equipmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<void> {
  await apiClient.delete(`${equipmentApiBase(equipmentType)}/${equipmentId}/image`);
}

export async function fetchManufacturingEquipmentImageUrl(
  equipmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<string> {
  const { data } = await apiClient.get<Blob>(`${equipmentApiBase(equipmentType)}/${equipmentId}/image`, {
    responseType: 'blob',
  });
  return window.URL.createObjectURL(data);
}

export async function fetchMainParts(equipmentId: number): Promise<EquipmentMainPart[]> {
  const { data } = await apiClient.get<EquipmentMainPart[]>(
    `/manufacturing-equipments/${equipmentId}/main-parts`
  );
  return data;
}

export async function createMainPart(
  equipmentId: number,
  payload: EquipmentMainPartPayload
): Promise<EquipmentMainPart> {
  const { data } = await apiClient.post<EquipmentMainPart>(
    `/manufacturing-equipments/${equipmentId}/main-parts`,
    payload
  );
  return data;
}

export async function updateMainPart(
  equipmentId: number,
  partId: number,
  payload: EquipmentMainPartPayload
): Promise<EquipmentMainPart> {
  const { data } = await apiClient.patch<EquipmentMainPart>(
    `/manufacturing-equipments/${equipmentId}/main-parts/${partId}`,
    payload
  );
  return data;
}

export async function deleteMainPart(equipmentId: number, partId: number): Promise<void> {
  await apiClient.delete(`/manufacturing-equipments/${equipmentId}/main-parts/${partId}`);
}

export async function fetchCalibrations(
  equipmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<EquipmentCalibration[]> {
  const { data } = await apiClient.get<EquipmentCalibration[]>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/calibrations`
  );
  return data;
}

export async function createCalibration(
  equipmentId: number,
  payload: EquipmentCalibrationPayload,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<EquipmentCalibration> {
  const { data } = await apiClient.post<EquipmentCalibration>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/calibrations`,
    payload
  );
  return data;
}

export async function updateCalibration(
  equipmentId: number,
  calibrationId: number,
  payload: EquipmentCalibrationPayload,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<EquipmentCalibration> {
  const { data } = await apiClient.patch<EquipmentCalibration>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/calibrations/${calibrationId}`,
    payload
  );
  return data;
}

export async function deleteCalibration(
  equipmentId: number,
  calibrationId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<void> {
  await apiClient.delete(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/calibrations/${calibrationId}`
  );
}

export async function uploadCalibrationAttachment(
  equipmentId: number,
  calibrationId: number,
  file: File,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<EquipmentCalibrationAttachment> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<EquipmentCalibrationAttachment>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/calibrations/${calibrationId}/attachments`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function downloadCalibrationAttachment(
  equipmentId: number,
  calibrationId: number,
  attachmentId: number,
  fileName: string,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/calibrations/${calibrationId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function deleteCalibrationAttachment(
  equipmentId: number,
  calibrationId: number,
  attachmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<void> {
  await apiClient.delete(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/calibrations/${calibrationId}/attachments/${attachmentId}`
  );
}

export async function fetchRepairs(equipmentId: number): Promise<EquipmentRepair[]> {
  const { data } = await apiClient.get<EquipmentRepair[]>(
    `/manufacturing-equipments/${equipmentId}/repairs`
  );
  return data;
}

export async function createRepair(
  equipmentId: number,
  payload: EquipmentRepairPayload
): Promise<EquipmentRepair> {
  const { data } = await apiClient.post<EquipmentRepair>(
    `/manufacturing-equipments/${equipmentId}/repairs`,
    payload
  );
  return data;
}

export async function updateRepair(
  equipmentId: number,
  repairId: number,
  payload: EquipmentRepairPayload
): Promise<EquipmentRepair> {
  const { data } = await apiClient.patch<EquipmentRepair>(
    `/manufacturing-equipments/${equipmentId}/repairs/${repairId}`,
    payload
  );
  return data;
}

export async function deleteRepair(equipmentId: number, repairId: number): Promise<void> {
  await apiClient.delete(`/manufacturing-equipments/${equipmentId}/repairs/${repairId}`);
}

export async function fetchStockParts(): Promise<ManufacturingStockPart[]> {
  const { data } = await apiClient.get<ManufacturingStockPart[]>('/manufacturing-stock-parts');
  return data;
}

export async function createStockPart(
  payload: ManufacturingStockPartPayload
): Promise<ManufacturingStockPart> {
  const { data } = await apiClient.post<ManufacturingStockPart>(
    '/manufacturing-stock-parts',
    payload
  );
  return data;
}

export async function updateStockPart(
  stockPartId: number,
  payload: ManufacturingStockPartPayload
): Promise<ManufacturingStockPart> {
  const { data } = await apiClient.patch<ManufacturingStockPart>(
    `/manufacturing-stock-parts/${stockPartId}`,
    payload
  );
  return data;
}

export async function refillStockPart(
  stockPartId: number,
  addQty: number
): Promise<ManufacturingStockPart> {
  const { data } = await apiClient.post<ManufacturingStockPart>(
    `/manufacturing-stock-parts/${stockPartId}/refill`,
    { addQty }
  );
  return data;
}

export async function deleteStockPart(stockPartId: number): Promise<void> {
  await apiClient.delete(`/manufacturing-stock-parts/${stockPartId}`);
}

export async function fetchEquipmentDocuments(
  equipmentId: number,
  docType: string
): Promise<EquipmentDocument[]> {
  const { data } = await apiClient.get<EquipmentDocument[]>(
    `/manufacturing-equipments/${equipmentId}/documents`,
    { params: { docType } }
  );
  return data;
}

export async function uploadEquipmentDocument(
  equipmentId: number,
  docType: string,
  file: File,
  options?: { title?: string; memo?: string }
): Promise<EquipmentDocument> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<EquipmentDocument>(
    `/manufacturing-equipments/${equipmentId}/documents`,
    formData,
    {
      params: {
        docType,
        title: options?.title || undefined,
        memo: options?.memo || undefined,
      },
      headers: { 'Content-Type': 'multipart/form-data' },
    }
  );
  return data;
}

export async function downloadEquipmentDocument(
  equipmentId: number,
  documentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/manufacturing-equipments/${equipmentId}/documents/${documentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}

export async function deleteEquipmentDocument(
  equipmentId: number,
  documentId: number
): Promise<void> {
  await apiClient.delete(`/manufacturing-equipments/${equipmentId}/documents/${documentId}`);
}

export async function fetchManagementStandard(
  equipmentId: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<EquipmentManagementStandard> {
  const { data } = await apiClient.get<EquipmentManagementStandard>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/management-standard`
  );
  return data;
}

export async function saveManagementStandard(
  equipmentId: number,
  payload: EquipmentManagementStandardPayload
): Promise<EquipmentManagementStandard> {
  const { data } = await apiClient.put<EquipmentManagementStandard>(
    `/manufacturing-equipments/${equipmentId}/management-standard`,
    payload
  );
  return data;
}

export async function fetchInspectionSheetYears(equipmentId: number): Promise<number[]> {
  const { data } = await apiClient.get<number[]>(
    `/manufacturing-equipments/${equipmentId}/inspection-sheets/years`
  );
  return data;
}

export async function fetchInspectionSheet(
  equipmentId: number,
  year: number,
  equipmentType: EquipmentType = 'MANUFACTURING'
): Promise<EquipmentInspectionSheet> {
  const { data } = await apiClient.get<EquipmentInspectionSheet>(
    `${equipmentApiBase(equipmentType)}/${equipmentId}/inspection-sheets/${year}`
  );
  return data;
}

export async function createInspectionSheet(
  equipmentId: number,
  year: number
): Promise<EquipmentInspectionSheet> {
  const { data } = await apiClient.post<EquipmentInspectionSheet>(
    `/manufacturing-equipments/${equipmentId}/inspection-sheets/${year}`
  );
  return data;
}

export async function saveInspectionSheet(
  equipmentId: number,
  year: number,
  payload: EquipmentInspectionSheetPayload
): Promise<EquipmentInspectionSheet> {
  const { data } = await apiClient.put<EquipmentInspectionSheet>(
    `/manufacturing-equipments/${equipmentId}/inspection-sheets/${year}`,
    payload
  );
  return data;
}

export async function confirmInspectionSheet(
  equipmentId: number,
  year: number
): Promise<EquipmentInspectionSheet> {
  const { data } = await apiClient.post<EquipmentInspectionSheet>(
    `/manufacturing-equipments/${equipmentId}/inspection-sheets/${year}/confirm`
  );
  return data;
}

export async function reloadInspectionSheetLines(
  equipmentId: number,
  year: number
): Promise<EquipmentInspectionSheet> {
  const { data } = await apiClient.post<EquipmentInspectionSheet>(
    `/manufacturing-equipments/${equipmentId}/inspection-sheets/${year}/reload-lines`
  );
  return data;
}


export async function downloadManagementStandardPdf(
  equipmentId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/manufacturing-equipments/${equipmentId}/management-standard/pdf`,
    fileName || `관리기준서_${equipmentId}.pdf`
  );
}

export async function downloadInspectionSheetPdf(
  equipmentId: number,
  year: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/manufacturing-equipments/${equipmentId}/inspection-sheets/${year}/pdf`,
    fileName || `점검시트_${equipmentId}_${year}.pdf`
  );
}
